import React, {ElementType, useCallback, useEffect} from 'react';
import {
  Button,
  ButtonGroup,
  Checkbox,
  FormControlLabel,
  Grid,
  Paper,
  PaperProps,
  Tooltip,
  Typography
} from '@mui/material';
import {Info} from '@mui/icons-material';
import {MONTHLY} from "../../../util/BookingUtil";
import CurrencyInput from "../../common/CurrencyInput/CurrencyInput";
import {styled} from "@mui/material/styles";

// type workaround for ts and/or mui styled not passing the component prop through
const ChargeOveragesFormPanel = styled(Paper)<PaperProps & { component: ElementType }>(({theme}) => ({
  padding: '2.14rem',
  borderRadius: '0.57rem',
  backgroundColor: theme.palette.grey[100],
}));

const ChargeOveragesGap10 = styled(Grid)(() => ({
  rowGap: '1.43rem',
}));

const ChargeOveragesFontColorBlack = styled(Typography)(({theme}) => ({
  color: theme.palette.common.black,
}));

const ChargeOveragesButtonGroup = styled(ButtonGroup)(() => ({
  display: 'flex',
  alignItems: 'center',
  background: "#fff",
  borderRadius: 8,
}));

const ChargeOveragesButton = styled(Button)(({theme}) => ({
  flexGrow: 1,
  [theme.breakpoints.up('sm')]: {
    width: 'auto',
    minWidth: '10.71em',
  }
}));

const ChargeOveragesButtonPadding = styled(Grid)(({theme}) => ({
  padding: 6,
  width: "100%",
  display: "flex",
  gap: 10,
  flexWrap: 'wrap' as const,
  justifyContent: 'center',
  [theme.breakpoints.up('sm')]: {
    flexWrap: 'nowrap' as const
  }
}));

interface ChargeOveragesFormProps {
  disabled?: boolean;
  locationChargeOverages?: boolean;
  chargeOverages: boolean | null;
  customerChargedAmountPerPeriod: string | number;
  partnerPayoutAmountPerPeriod: string | number;
  overageCustomerChargeRate: string | number;
  overagePartnerPayoutRate: string | number;
  onCheckboxChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onChange: (name: string, value: any) => void;
  durationType: string;
  manualPayment?: boolean;
  setBrokeredSettingsIsValid: (isValid: boolean) => void;
}

const ChargeOveragesForm: React.FC<ChargeOveragesFormProps> = (props) => {
  const {
    disabled,
    locationChargeOverages,
    chargeOverages,
    customerChargedAmountPerPeriod,
    partnerPayoutAmountPerPeriod,
    overageCustomerChargeRate,
    overagePartnerPayoutRate,
    onCheckboxChange,
    onChange,
    durationType,
    manualPayment,
    setBrokeredSettingsIsValid
  } = props;
  const onChargeOveragesToggle = useCallback((value: boolean) => {
    onChange('chargeOverages', value);
  }, [onChange]);

  const handleCurrencyChange = (name: string) => (value: number) => {
    onChange(name, value);
  };

  useEffect(() => {
    onChargeOveragesToggle(chargeOverages === null ? !!locationChargeOverages : chargeOverages);
  }, [onChargeOveragesToggle, chargeOverages, locationChargeOverages]);

  const validate_partnerPayoutAmountPerPeriod = useCallback((): string => {
    if ((partnerPayoutAmountPerPeriod as number) > (customerChargedAmountPerPeriod as number)) {
      return 'Supplier payout amount is greater than buyer charged amount';
    }

    return '';
  }, [customerChargedAmountPerPeriod, partnerPayoutAmountPerPeriod]);

  const validate_overageCustomerChargeRate = useCallback((): string => {
    if (chargeOverages && (typeof overageCustomerChargeRate != 'string') && !((overageCustomerChargeRate as number) > 0)) {
      return 'When overages are charged, Customer Charge Rate must be greater than 0';
    }

    return '';
  }, [chargeOverages, overageCustomerChargeRate]);

  const validate_overagePartnerPayoutRate = useCallback((): string => {
    if (chargeOverages && typeof overagePartnerPayoutRate != 'string') {
      if (!((overagePartnerPayoutRate as number) > 0)) {
        return 'When overages are charged, Supplier Payout Rate must be greater than 0';
      } else if ((overagePartnerPayoutRate as number) > (overageCustomerChargeRate as number)) {
        return 'Supplier Payout Rate may not exceed Customer Charge Rate';
      }
    }

    return '';
  }, [chargeOverages, overageCustomerChargeRate, overagePartnerPayoutRate]);

  useEffect(() => {
    setBrokeredSettingsIsValid(
      typeof customerChargedAmountPerPeriod != 'string' &&
      typeof partnerPayoutAmountPerPeriod != 'string' &&
      (
        !chargeOverages || (
          typeof overageCustomerChargeRate != 'string' &&
          typeof overagePartnerPayoutRate != 'string'
        )
      ) &&
      typeof chargeOverages != 'string' &&
      !validate_partnerPayoutAmountPerPeriod() &&
      !validate_overageCustomerChargeRate() &&
      !validate_overagePartnerPayoutRate()
    )
  }, [chargeOverages, customerChargedAmountPerPeriod, partnerPayoutAmountPerPeriod, overageCustomerChargeRate, overagePartnerPayoutRate, setBrokeredSettingsIsValid, validate_partnerPayoutAmountPerPeriod, validate_overageCustomerChargeRate, validate_overagePartnerPayoutRate]);

  const overagesStatusText = `Location Overages is turned ${disabled ? 'OFF' : 'ON'}`;
  const bookingDuration = durationType === MONTHLY ? 'Month' : 'Day';
  const manualPaymentText = 'A booking set to \'Manual Payment\' means that invoices will be generated (booking invoices and overage invoices), but the accounting team will need to manually send an invoice to the buyer, collect funds and payout the supplier outside of the SecurSpace system.';

  return (
    <ChargeOveragesFormPanel component='section'>
      <ChargeOveragesGap10 container direction='column'>
        <Grid item container justifyContent='space-between' alignItems='center'>
          <ChargeOveragesFontColorBlack variant='subtitle2'>Charge Overages</ChargeOveragesFontColorBlack>
          <Tooltip title={overagesStatusText} placement='left'>
            <Info color='primary' />
          </Tooltip>
        </Grid>
        <ChargeOveragesButtonGroup aria-label="outlined primary button group">
          <ChargeOveragesButtonPadding>
            <ChargeOveragesButton
              className='d-flex ss-tab-btn'
              onClick={() => onChargeOveragesToggle(false)}
              variant={!chargeOverages ? "contained" : "outlined"}
              color={!chargeOverages ? "primary" : "secondary"}
              disabled={disabled}
            >
              No
            </ChargeOveragesButton>
            <ChargeOveragesButton
              className='d-flex ss-tab-btn'
              onClick={() => onChargeOveragesToggle(true)}
              variant={chargeOverages ? "contained" : "outlined"}
              color={chargeOverages ? "primary" : "secondary"}
              disabled={disabled}
            >
              Yes
            </ChargeOveragesButton>
          </ChargeOveragesButtonPadding>
        </ChargeOveragesButtonGroup>

        <Grid item>
          <Grid item container justifyContent='space-between' alignItems='center'>
            <FormControlLabel
              label={<Typography variant='body2' color='textPrimary'>Manual Payment</Typography>}
              control={<Checkbox name='manualPayment' checked={manualPayment} onChange={onCheckboxChange} />}
            />
            <Tooltip title={manualPaymentText} placement='left'>
              <Info color='primary' />
            </Tooltip>
          </Grid>
        </Grid>

        <CurrencyInput
          name='customerChargedAmountPerPeriod'
          value={customerChargedAmountPerPeriod as number}
          label={`Customer Charged Amount Per ${bookingDuration}`}
          placeholder={`Enter the buyer charge /${bookingDuration}`}
          onChange={handleCurrencyChange('customerChargedAmountPerPeriod')}
          fullWidth
          required
          variant='standard'
        />
        <CurrencyInput
          name='partnerPayoutAmountPerPeriod'
          value={partnerPayoutAmountPerPeriod as number}
          label={`Supplier Payout Amount Per ${bookingDuration}`}
          placeholder={`Enter the Supplier payout /${bookingDuration}`}
          onChange={handleCurrencyChange('partnerPayoutAmountPerPeriod')}
          error={!!validate_partnerPayoutAmountPerPeriod()}
          helperText={validate_partnerPayoutAmountPerPeriod()}
          fullWidth
          required
          variant='standard'
        />
        <CurrencyInput
          name='overageCustomerChargeRate'
          value={overageCustomerChargeRate as number}
          label='Overage Customer Charge Rate'
          placeholder='Enter the Customer Overage rate'
          onChange={handleCurrencyChange('overageCustomerChargeRate')}
          fullWidth
          required
          error={!!validate_overageCustomerChargeRate()}
          helperText={validate_overageCustomerChargeRate()}
          disabled={!chargeOverages}
          variant='standard'
        />
        <CurrencyInput
          name='overagePartnerPayoutRate'
          value={overagePartnerPayoutRate as number}
          label='Overage Supplier Payout Rate'
          placeholder='Enter the Supplier Overage payout'
          onChange={handleCurrencyChange('overagePartnerPayoutRate')}
          fullWidth
          required
          error={!!validate_overagePartnerPayoutRate()}
          helperText={validate_overagePartnerPayoutRate()}
          disabled={!chargeOverages}
          variant='standard'
        />
      </ChargeOveragesGap10>
    </ChargeOveragesFormPanel>
  );
};

export default ChargeOveragesForm;
